import { Package, Pencil, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import { toast } from '@/hooks/use-toast';
import { useDeleteProduct } from '@/hooksApi/useProductCatalog';
import { ProductCatalogItem } from '@/types/productCatalog';

interface ProductRowProps {
  product: ProductCatalogItem;
  onEdit: (uid: string) => void;
}

export const ProductRow = ({ product, onEdit }: ProductRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteProduct, isLoading: isDeleting } = useDeleteProduct();

  const handleDelete = () => {
    if (isDeleting) return;
    deleteProduct(product.uid, {
      onSuccess: () => {
        queryClient.invalidateQueries(['product-catalog']);
        toast({ title: String(t('product_deleted', { defaultValue: 'Product deleted' })) });
      },
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm transition-shadow hover:shadow-md'>
      <div className='p-5 flex items-start gap-4'>
        <div className='h-10 w-10 rounded-lg bg-sd-muted flex items-center justify-center shrink-0 mt-0.5'>
          <Package className='h-5 w-5 text-sd-muted-foreground' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 mb-1'>
            <h3 className='text-sm font-semibold text-sd-foreground m-0'>{product.title}</h3>
            {product.category && (
              <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-sd-muted text-xs font-medium text-sd-muted-foreground'>
                {product.category}
              </span>
            )}
            {product.is_fragile && (
              <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[rgba(245,158,11,0.1)] text-xs font-medium text-[rgb(217,119,6)]'>
                <ShieldAlert className='h-3 w-3' />
                {String(t('fragile', { defaultValue: 'Fragile' }))}
              </span>
            )}
          </div>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5'>
            <p className='text-sm font-medium text-sd-foreground m-0' dir='ltr'>
              {product.price !== null
                ? `${String(t('iqd', { defaultValue: 'IQD' }))} ${product.price.toLocaleString()}`
                : String(t('no_price_set', { defaultValue: 'No price set' }))}
            </p>
            {/* Quantity hidden — always 1. Uncomment to re-enable. */}
          </div>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <button
            onClick={() => onEdit(product.uid)}
            aria-label={String(t('edit_product', { defaultValue: 'Edit product' }))}
            className='h-8 w-8 rounded-lg border border-sd-input bg-sd-background flex items-center justify-center hover:bg-sd-muted transition-colors'
          >
            <Pencil className='h-3.5 w-3.5 text-sd-muted-foreground' />
          </button>
          <ConfirmDelete
            title={String(t('delete_product', { defaultValue: 'Delete product' }))}
            description={String(
              t('delete_product_description', {
                defaultValue: 'Are you sure you want to delete this product? This action cannot be undone.',
              }),
            )}
            onConfirm={handleDelete}
            disabled={isDeleting}
          />
        </div>
      </div>
    </div>
  );
};
